import React, { ReactElement, useEffect, useState } from 'react';
import { useSnackbar } from 'notistack';
import { observer } from 'mobx-react';
import { Box, Fade, useMediaQuery, useTheme } from '@material-ui/core';
import SyncIcon from '@material-ui/icons/Sync';
import PlaylistAddOutlinedIcon from '@material-ui/icons/PlaylistAddOutlined';
import { useAppContext } from 'contexts/AppContext';
import { emailIsValidRe, wait } from 'globals/utils';
import defaultLocales from 'globals/locales/default';
import FsPaper from 'ui-elements/main-elements/FsPaper';
import FsPendingButton from 'ui-elements/buttons/FsPendingButton';
import FsFormGridItem from 'ui-elements/form-elements/FsFormGridItem';
import FsFormGridWrapper from 'ui-elements/form-elements/FsFormGridWrapper';
import FsConfirmationDialog from 'ui-elements/modals/FsConfirmationDialog';
import FsButton from 'ui-elements/buttons/FsButton';
import FsBody from 'ui-elements/typography/FsBody';
import FsBodyBold from 'ui-elements/typography/FsBodyBold';
import { NotificationSettingsSingleRowDataProps } from './NotificationSettingsSingleRow/NotificationSettingsSingleRow';
import NotificationSettingsSingleRow from './NotificationSettingsSingleRow';
import { AddNewButton } from './NotificationSettings.style-elements';

export interface SingleNotification {
  email: string;
}

export interface NotificationSettingsDataProps {
  title: string;
  subTitle?: ReactElement;
}

export interface NotificationSettingsProps
  extends NotificationSettingsDataProps {
  inStatus: boolean;
  timeout?: number;
}

const NotificationSettings: React.FC<NotificationSettingsProps> = ({
  title,
  subTitle,
  inStatus,
  timeout = 1000,
}) => {
  const {
    notificationEmails: {
      getNotificationEmailsRows,
      removeNotificationEmail,
      addNotificationEmail,
      updateNotificationEmail,
      init: initNotificationEmails,
      update: updateNotificationEmails,
    },
  } = useAppContext();
  const rows = getNotificationEmailsRows();
  const { enqueueSnackbar } = useSnackbar();
  const [pending, setPending] = useState<boolean>(false);
  const [showConfirmationDialog, setShowConfirmationDialog] = useState<boolean>(
    false
  );
  const suspiciousEmails = rows.filter(
    ({ email }) => !!email && !email.match(emailIsValidRe)
  );
  const theme = useTheme();
  const isUpXsMobile = useMediaQuery(theme.breakpoints.up(375), {
    noSsr: true,
  });

  const handleChange = (
    changedField: NotificationSettingsSingleRowDataProps
  ) => {
    updateNotificationEmail(changedField);
  };

  const handleAdd = () => {
    addNotificationEmail();
  };

  const handleRemove = (selectedId: number) => {
    removeNotificationEmail(selectedId);
  };

  const getCleanListOfEmails = () => {
    return rows.filter(({ email }) => !!email && !!email.match(emailIsValidRe));
  };

  const handleConfirm = async () => {
    const payload = getCleanListOfEmails();
    // eslint-disable-next-line no-console
    console.log('UPDATE:', payload);
    setShowConfirmationDialog(false);
    setPending(true);
    await wait(1000);
    setPending(false);
    updateNotificationEmails();
    enqueueSnackbar(defaultLocales.updateEmailNotificationSuccess, {
      variant: 'success',
    });
  };

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (suspiciousEmails.length > 0) {
      setShowConfirmationDialog(true);
      return;
    }
    await handleConfirm();
  };

  useEffect(() => {
    initNotificationEmails();
  }, [initNotificationEmails]);

  return (
    <Fade in={inStatus} timeout={timeout}>
      <FsPaper title={title} subTitle={subTitle}>
        <Box maxWidth={600}>
          <Box marginBottom={2}>
            {/* <pre>{JSON.stringify(rows, null, 2)}</pre> */}
            <div>
              {rows.map(({ id, ...otherProps }, idx) => (
                <NotificationSettingsSingleRow
                  {...{ id, ...otherProps }}
                  key={id}
                  isFirst={!idx}
                  onRemove={() => handleRemove(id)}
                  onChange={(changedField) => handleChange(changedField)}
                />
              ))}
            </div>
            <Box marginBottom={0.5}>
              <FsFormGridWrapper>
                <FsFormGridItem>
                  <AddNewButton
                    fullWidth
                    color="primary"
                    startIcon={<PlaylistAddOutlinedIcon />}
                    onClick={handleAdd}
                  >
                    {isUpXsMobile
                      ? defaultLocales.addAnother
                      : defaultLocales.add}
                  </AddNewButton>
                </FsFormGridItem>
                <FsFormGridItem style={{ flexGrow: 0 }}>
                  <Box minWidth={48} />
                </FsFormGridItem>
              </FsFormGridWrapper>
            </Box>
          </Box>
          <FsPendingButton
            pending={pending}
            onClick={handleUpdate}
            startIcon={<SyncIcon />}
          >
            {defaultLocales.update}
          </FsPendingButton>
        </Box>

        <FsConfirmationDialog
          title={defaultLocales.confirmNotifications}
          showModal={showConfirmationDialog}
          setShowModal={() => setShowConfirmationDialog(false)}
          actionButtons={[
            <FsButton key={1} onClick={handleConfirm}>
              {defaultLocales.yes}, {defaultLocales.continue.toLowerCase()}
            </FsButton>,
            <FsButton
              key={2}
              onClick={() => setShowConfirmationDialog(false)}
              color="primary"
              variant="contained"
            >
              {defaultLocales.no}
            </FsButton>,
          ]}
        >
          <FsBody>{defaultLocales.sendInvitationsInvalidEmailsQuestion}</FsBody>
          <Box mt={2}>
            <FsBodyBold>{defaultLocales.suspiciousEmails}:</FsBodyBold>
            {suspiciousEmails.map(({ id, email }) => (
              <FsBody key={id}> - {email}</FsBody>
            ))}
          </Box>
        </FsConfirmationDialog>
      </FsPaper>
    </Fade>
  );
};

export default observer(NotificationSettings);
